import type { SuretyBondFormTemplateId } from "@inrev/common";
import {
	type BondFormTemplate,
	useOpenBondFormTemplatePreview,
} from "@portal/api/bond-request/agent";
import type { FC } from "react";
import { bondFormTypeLabelMap } from "../../../../constants";
import { Modal } from "../../../layout/Modal";
import { ModalItemWithHeader } from "../../../layout/ModalItemWithHeader";
import { Button, Icon, Text } from "../../../primitives";

export type BondFormLibraryModalProps = {
	templates: BondFormTemplate[];
	onSelect: (templateId: SuretyBondFormTemplateId) => void;
	onClose: () => void;
};

export const BondFormLibraryModal: FC<BondFormLibraryModalProps> = ({
	templates,
	onSelect,
	onClose,
}) => {
	const { openPreview } = useOpenBondFormTemplatePreview();

	return (
		<Modal onClickOutside={onClose} itemClassName="mb-[5%]">
			<ModalItemWithHeader
				header={
					<div className="flex items-center space-x-[8px]">
						<Icon type="book-open" size="mini" color="secondary" />
						<div>Bond Form Library</div>
					</div>
				}
				className="w-[550px] top-1/4 bottom-1/4"
				onClose={onClose}
			>
				<div className="w-full max-h-[85vh] pt-[20px] pb-[35px] flex">
					<div className="w-full max-h-full flex flex-col space-y-[15px] px-[35px] overflow-y-auto">
						<Text type="body" color="secondary" className="w-full">
							Select from {templates.length} bond form template
							{templates.length === 1 ? "" : "s"}
						</Text>
						<div className="w-full h-full flex flex-col space-y-[10px]">
							{templates.map((template) => (
								<div
									key={template.id}
									data-testid="bond-form-library-row"
									className="w-full flex-shrink-0 flex items-center rounded-sm bg-surface-light border border-bc-default px-[20px] py-[15px]"
								>
									<div className="flex-1 flex flex-col space-y-[10px]">
										<div className="flex flex-col gap-2.5">
											<Text type="body-strong" color="primary">
												{template.name}
											</Text>
											<Text type="caption" color="tertiary">
												{bondFormTypeLabelMap[template.type]}
											</Text>
										</div>
										<div className="flex gap-[5px]">
											{template.tags.map((tag) => (
												<Text
													key={tag}
													type="caption"
													color="secondary"
													className="px-[8px] py-[3px] bg-surface-base rounded-sm border border-bc-strong"
												>
													{tag}
												</Text>
											))}
										</div>
									</div>
									<div className="flex flex-col space-y-[5px]">
										<Button
											color="secondary"
											variant="outline"
											size="sm"
											onClick={() => openPreview(template.id)}
										>
											Preview
										</Button>
										<Button color="primary" size="sm" onClick={() => onSelect(template.id)}>
											Select
										</Button>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
